import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

export const LOCALES = ['en', 'fr', 'th', 'lo', 'vi', 'zh'] as const
export type Locale = (typeof LOCALES)[number]
export const DEFAULT_LOCALE: Locale = 'en'

// Native-script labels for the switcher.
export const LOCALE_LABELS: Record<Locale, string> = {
  en: 'English',
  fr: 'Français',
  th: 'ไทย',
  lo: 'ລາວ',
  vi: 'Tiếng Việt',
  zh: '中文',
}

export const isLocale = (v: string | undefined): v is Locale =>
  !!v && (LOCALES as readonly string[]).includes(v)

// English is bundled eagerly so init is synchronous (the app always renders, no
// translation-key flash) and EN doubles as the fallback language. The other five
// locales are emitted as separate on-demand chunks — switching language is a full
// page reload to the locale-prefixed URL (see LanguageSwitcher), so each load only
// ever needs ONE language. This keeps ~330KB of unused locale JSON out of the
// initial bundle. Drop a new src/i18n/locales/<lang>/<ns>.json and it is picked up.
const eagerEn = import.meta.glob('./locales/en/*.json', {
  eager: true,
}) as Record<string, { default: Record<string, unknown> }>
// Non-EN locales: lazy () => import() thunks, one chunk each, fetched on demand.
const lazyLocales = import.meta.glob([
  './locales/*/*.json',
  '!./locales/en/*.json',
]) as Record<string, () => Promise<{ default: Record<string, unknown> }>>

const nsOf = (p: string) => p.match(/\/([^/]+)\.json$/)?.[1] ?? ''
const langOf = (p: string) => p.match(/\/locales\/([^/]+)\//)?.[1] ?? ''

const resources: Record<string, Record<string, Record<string, unknown>>> = {
  en: {},
}
const namespaces = new Set<string>()
for (const path in eagerEn) {
  const ns = nsOf(path)
  if (!ns) continue
  resources.en[ns] = eagerEn[path].default
  namespaces.add(ns)
}

// Fetch a non-EN locale's namespace bundles and register them, then re-emit
// languageChanged so react-i18next re-renders with the real translations.
const loaded = new Set<string>(['en'])
export async function loadLocale(lang: string) {
  if (loaded.has(lang) || !isLocale(lang)) return
  loaded.add(lang)
  await Promise.all(
    Object.entries(lazyLocales)
      .filter(([path]) => langOf(path) === lang)
      .map(async ([path, load]) => {
        i18n.addResourceBundle(
          lang,
          nsOf(path),
          (await load()).default,
          true,
          true
        )
      })
  )
  if (i18n.language === lang) i18n.changeLanguage(lang)
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: LOCALES as unknown as string[],
    ns: Array.from(namespaces),
    defaultNS: 'common',
    interpolation: { escapeValue: false },
    // The URL prefix is the source of truth (segment 0). EN sits at the root with
    // no prefix, so on '/' or '/tours' the path lookup finds no locale and we fall
    // through to the saved cookie, then the visitor's browser language.
    detection: {
      order: ['path', 'cookie', 'navigator', 'htmlTag'],
      lookupFromPathIndex: 0,
      lookupCookie: 'tla_lang',
      caches: ['cookie'],
    },
    react: { useSuspense: false },
  })

// The detector may resolve a non-EN language from the URL/cookie/browser. EN is
// already loaded; fetch the detected locale's chunks now (a brief EN fallback shows
// until they arrive). Also covers any future in-app changeLanguage call.
if (i18n.language !== 'en') void loadLocale(i18n.language)
i18n.on('languageChanged', (lng) => void loadLocale(lng))

// Keep <html lang> in sync with the active locale (a11y + SEO + per-script fonts).
if (typeof document !== 'undefined') {
  const applyHtmlLang = (lng: string) => {
    document.documentElement.lang = lng
  }
  applyHtmlLang(i18n.language)
  i18n.on('languageChanged', applyHtmlLang)
}

export default i18n
